"use client";

import type { LucideIcon } from "lucide-react";
import clsx from "clsx";
import { ChevronDown, Gift, LayoutDashboard, LifeBuoy, MapPin, Package, Sparkles } from "lucide-react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { Button } from "@/ui/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";

export function AccountPortalChrome({ children }: { children: React.ReactNode }) {
	const pathname = usePathname() ?? "";
	const t = useTranslations("account");

	const afterChannel = pathname.replace(/^\/[^/]+\/[^/]+/, "") || "/";
	const isLoyalty = afterChannel.startsWith("/account/loyalty");
	const isReferrals = afterChannel.startsWith("/account/referrals");
	const isAddresses = afterChannel.startsWith("/account/addresses");
	const isSupport = afterChannel.startsWith("/account/support");
	const isOrders = afterChannel === "/orders" || afterChannel.startsWith("/orders/");
	const isOverview =
		(afterChannel === "/account" || afterChannel === "/account/") &&
		!isLoyalty &&
		!isReferrals &&
		!isAddresses &&
		!isSupport;

	const currentLabel = isOrders
		? t("navOrders")
		: isReferrals
			? t("navReferrals")
			: isSupport
				? t("navSupport")
				: isAddresses
					? t("navAddresses")
					: isLoyalty
						? t("navLoyalty")
						: t("navOverview");

	return (
		<div className="min-h-[calc(100dvh-56px)] lg:min-h-[calc(100dvh-72px)]">
			{/* Mobile: ein kompaktes Menü oben (kein zweites horizontales Tab-Band) */}
			<div className="bg-muted/50 border-b border-border md:hidden">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<p className="pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						{t("navSectionTitle")}
					</p>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								type="button"
								variant="secondary"
								className="mb-3 mt-2 w-full justify-between gap-2"
								aria-label={t("a11yNavLabel")}
							>
								<span className="truncate">{currentLabel}</span>
								<ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="start"
							className="w-[min(100vw-2rem,var(--radix-dropdown-menu-trigger-width))]"
						>
							<DropdownMenuItem asChild>
								<LinkWithChannel href="/account" prefetch className={clsx(isOverview && "bg-accent")}>
									{t("navOverview")}
								</LinkWithChannel>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<LinkWithChannel href="/orders" prefetch className={clsx(isOrders && "bg-accent")}>
									{t("navOrders")}
								</LinkWithChannel>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<LinkWithChannel
									href="/account/referrals"
									prefetch
									className={clsx(isReferrals && "bg-accent")}
								>
									{t("navReferrals")}
								</LinkWithChannel>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<LinkWithChannel href="/account/support" prefetch className={clsx(isSupport && "bg-accent")}>
									{t("navSupport")}
								</LinkWithChannel>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<LinkWithChannel
									href="/account/addresses"
									prefetch
									className={clsx(isAddresses && "bg-accent")}
								>
									{t("navAddresses")}
								</LinkWithChannel>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<LinkWithChannel href="/account/loyalty" prefetch className={clsx(isLoyalty && "bg-accent")}>
									{t("navLoyalty")}
								</LinkWithChannel>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Desktop: linke Sidebar — Navigation nicht mehr unter dem Header duplizieren */}
			<div className="mx-auto flex w-full max-w-7xl flex-1 flex-col md:flex-row md:items-stretch">
				<aside className="bg-muted/30 hidden shrink-0 border-border md:block md:w-56 md:border-r lg:w-60">
					<div className="sticky top-[56px] py-8 pl-4 pr-3 lg:top-[72px] lg:pl-6 lg:pr-4">
						<p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
							{t("navSectionTitle")}
						</p>
						<nav className="mt-4 flex flex-col gap-1" aria-label={t("a11yNavLabel")}>
							<AccountSidebarLink href="/account" active={isOverview} icon={LayoutDashboard}>
								{t("navOverview")}
							</AccountSidebarLink>
							<AccountSidebarLink href="/orders" active={isOrders} icon={Package}>
								{t("navOrders")}
							</AccountSidebarLink>
							<AccountSidebarLink href="/account/referrals" active={isReferrals} icon={Gift}>
								{t("navReferrals")}
							</AccountSidebarLink>
							<AccountSidebarLink href="/account/support" active={isSupport} icon={LifeBuoy}>
								{t("navSupport")}
							</AccountSidebarLink>
							<AccountSidebarLink href="/account/addresses" active={isAddresses} icon={MapPin}>
								{t("navAddresses")}
							</AccountSidebarLink>
							<AccountSidebarLink href="/account/loyalty" active={isLoyalty} icon={Sparkles}>
								{t("navLoyalty")}
							</AccountSidebarLink>
						</nav>
					</div>
				</aside>
				<div className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-8">{children}</div>
			</div>
		</div>
	);
}

function AccountSidebarLink({
	href,
	active,
	icon: Icon,
	children,
}: {
	href: string;
	active: boolean;
	icon: LucideIcon;
	children: React.ReactNode;
}) {
	return (
		<LinkWithChannel
			href={href}
			prefetch
			className={clsx(
				"flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
				active
					? "bg-background text-foreground shadow-sm ring-1 ring-border"
					: "hover:bg-background/80 text-muted-foreground hover:text-foreground",
			)}
		>
			<Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
			{children}
		</LinkWithChannel>
	);
}
